import React, { useState, useCallback } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Switch,
  Typography,
  Button,
  FormControl,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { LinkOutlined } from '@mui/icons-material';
import StripeCustomerModal from './StripeCustomerModal';
import { validateBillingType } from '../../settings/components/PaymentMethods/paymentApi';

interface BillingConfigProps {
  org: {
    id: string;
    billingType?: string;
    planType?: string;
    salesChannel?: string;
    selfServiceCancellation?: boolean;
    paymentCustomerID?: string;
  };
  onChange: (field: string, value: any) => void;
  onSavedExternally?: (fields: Record<string, any>) => void;
}

const SALES_CHANNEL_DEFAULTS: Record<string, boolean> = {
  plg: true,
  sales: false,
};

export default function BillingConfig({
  org,
  onChange,
  onSavedExternally,
}: BillingConfigProps) {
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [selfServiceOverridden, setSelfServiceOverridden] = useState(false);
  const [billingTypeError, setBillingTypeError] = useState('');
  const [billingTypeStripeUrl, setBillingTypeStripeUrl] = useState('');
  const [validatingBillingType, setValidatingBillingType] = useState(false);

  // When billing type changes, validate seat_based against Stripe before applying
  const handleBillingTypeChange = useCallback(
    async (newBillingType: string) => {
      setBillingTypeError('');
      setBillingTypeStripeUrl('');

      if (newBillingType !== 'seat_based') {
        onChange('billingType', newBillingType);
        return;
      }

      // Validate that the org has an active subscription before allowing seat_based
      setValidatingBillingType(true);
      try {
        const result = await validateBillingType(org.id, newBillingType);

        if (result.success && result.data?.valid) {
          onChange('billingType', newBillingType);
        } else {
          const errorCode = result.error || '';
          const message =
            result.data?.message ||
            'Cannot enable seat-based pricing for this org.';
          setBillingTypeError(message);
          setBillingTypeStripeUrl(result.data?.stripeCustomerUrl || '');

          // If there's no Stripe customer at all, don't show a Stripe link
          if (errorCode === 'NO_STRIPE_CUSTOMER') {
            setBillingTypeStripeUrl('');
          }
        }
      } catch (err) {
        setBillingTypeError(
          'Failed to validate billing type. Please try again.'
        );
      } finally {
        setValidatingBillingType(false);
      }
    },
    [onChange, org.id]
  );

  // When salesChannel changes and the toggle hasn't been manually overridden,
  // auto-update selfServiceCancellation to the new default
  const handleSalesChannelChange = useCallback(
    (newChannel: string) => {
      onChange('salesChannel', newChannel);
      if (!selfServiceOverridden) {
        const newDefault = SALES_CHANNEL_DEFAULTS[newChannel] ?? false;
        onChange('selfServiceCancellation', newDefault);
      }
    },
    [onChange, selfServiceOverridden]
  );

  // Handle manual toggle of selfServiceCancellation
  const handleSelfServiceToggle = useCallback(() => {
    setSelfServiceOverridden(true);
    onChange('selfServiceCancellation', !org.selfServiceCancellation);
  }, [onChange, org.selfServiceCancellation]);

  // Handle successful Stripe customer link from the modal
  const handleStripeCustomerLinked = useCallback(
    (stripeCustomerID: string, billingType?: string) => {
      const savedFields: Record<string, any> = {
        paymentCustomerID: stripeCustomerID,
      };
      if (billingType) {
        savedFields.billingType = billingType;
      }

      // Update form state
      onChange('paymentCustomerID', stripeCustomerID);
      if (billingType) {
        onChange('billingType', billingType);
      }

      // Update baseline so these don't show as unsaved changes
      onSavedExternally?.(savedFields);

      setStripeModalOpen(false);
    },
    [onChange, onSavedExternally]
  );

  const selectMenuProps = {
    PaperProps: {
      sx: { backgroundColor: '#fff' },
    },
  };

  return (
    <Box>
      <Typography
        style={{ fontWeight: 'bold', fontSize: '1.5rem', marginBottom: '8px' }}
      >
        Billing
      </Typography>

      {/* Billing Type */}
      <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
        <Typography style={{ width: '12vw', fontWeight: 'bold' }}>
          Billing Type
        </Typography>
        <FormControl size="small" style={{ width: '200px' }}>
          <Select
            variant="outlined"
            value={org.billingType || ''}
            onChange={(e) => handleBillingTypeChange(e.target.value as string)}
            displayEmpty
            disabled={validatingBillingType}
            MenuProps={selectMenuProps}
            sx={{ backgroundColor: '#fff' }}
          >
            <MenuItem value="">
              <em>Not Set</em>
            </MenuItem>
            <MenuItem value="seat_based">Seat Based</MenuItem>
            <MenuItem value="fixed_rate">Fixed Rate</MenuItem>
          </Select>
        </FormControl>
        {validatingBillingType && <CircularProgress size={20} sx={{ ml: 2 }} />}
      </Box>

      {/* Billing Type Validation Error */}
      {billingTypeError && (
        <Box sx={{ mx: '5px', mb: 1, maxWidth: '500px' }}>
          <Alert severity="warning" onClose={() => setBillingTypeError('')}>
            {billingTypeError}
            {billingTypeStripeUrl && (
              <>
                {' '}
                <Link
                  href={billingTypeStripeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View customer in Stripe
                </Link>
              </>
            )}
          </Alert>
        </Box>
      )}

      {/* Plan Type */}
      <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
        <Typography style={{ width: '12vw', fontWeight: 'bold' }}>
          Plan Type
        </Typography>
        <FormControl size="small" style={{ width: '200px' }}>
          <Select
            variant="outlined"
            value={org.planType || ''}
            onChange={(e) => onChange('planType', e.target.value)}
            displayEmpty
            MenuProps={selectMenuProps}
            sx={{ backgroundColor: '#fff' }}
          >
            <MenuItem value="">
              <em>Not Set</em>
            </MenuItem>
            <MenuItem value="starter">Starter</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
            <MenuItem value="enterprise">Enterprise</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Sales Channel */}
      <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
        <Typography style={{ width: '12vw', fontWeight: 'bold' }}>
          Sales Channel
        </Typography>
        <FormControl size="small" style={{ width: '200px' }}>
          <Select
            variant="outlined"
            value={org.salesChannel || ''}
            onChange={(e) => handleSalesChannelChange(e.target.value as string)}
            displayEmpty
            MenuProps={selectMenuProps}
            sx={{ backgroundColor: '#fff' }}
          >
            <MenuItem value="">
              <em>Not Set</em>
            </MenuItem>
            <MenuItem value="plg">PLG</MenuItem>
            <MenuItem value="sales">Sales</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Self Service Cancellation */}
      <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
        <Typography style={{ width: '12vw', fontWeight: 'bold' }}>
          Self Service Cancellation
        </Typography>
        <Switch
          checked={org.selfServiceCancellation || false}
          onChange={handleSelfServiceToggle}
        />
        {selfServiceOverridden && (
          <Typography
            variant="caption"
            color="text.secondary"
            style={{ marginLeft: '8px' }}
          >
            (manually overridden)
          </Typography>
        )}
      </Box>

      {/* Payment Customer ID (read-only) */}
      <Box style={{ display: 'flex', alignItems: 'center', margin: '5px' }}>
        <Typography style={{ width: '12vw', fontWeight: 'bold' }}>
          Payment Customer ID
        </Typography>
        <Typography
          style={{ color: org.paymentCustomerID ? 'inherit' : '#999' }}
        >
          {org.paymentCustomerID || 'Not linked'}
        </Typography>
        {org.paymentCustomerID && (
          <a
            href={`https://dashboard.stripe.com/customers/${org.paymentCustomerID}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: '12px', fontSize: '0.875rem' }}
          >
            View in Stripe
          </a>
        )}
      </Box>

      {/* Link Stripe Customer Button */}
      <Box style={{ margin: '10px 5px' }}>
        <Button
          variant="outlined"
          startIcon={<LinkOutlined />}
          onClick={() => setStripeModalOpen(true)}
        >
          Link Stripe Customer
        </Button>
      </Box>

      {/* Stripe Customer Modal */}
      <StripeCustomerModal
        open={stripeModalOpen}
        onClose={() => setStripeModalOpen(false)}
        orgID={org.id}
        onCustomerLinked={handleStripeCustomerLinked}
      />
    </Box>
  );
}
